import React from 'react';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Table from '@cloudscape-design/components/table';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';

import {
  RecentRunItem,
  RecentViewType,
  getRecentRuns,
  clearRecentRuns,
} from '../api/recentRunsApi';

function formatRelativeTime(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

interface RecentRunsApiPanelProps {
  viewType: RecentViewType;
  onLoadRun: (run: RecentRunItem) => void;
  refreshKey?: number;
}

// Generic recent-runs panel for any view, backed by DynamoDB via recentRunsApi.
// Columns are driven by the item's `label` (built per view when the run is saved),
// so a single panel works for demand/lifespan/ig/perturbation/divergence.
export default function RecentRunsApiPanel({
  viewType,
  onLoadRun,
  refreshKey,
}: RecentRunsApiPanelProps): JSX.Element {
  const [runs, setRuns] = React.useState<RecentRunItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRecentRuns(viewType)
      .then((items) => { if (!cancelled) setRuns(items); })
      .catch(() => { if (!cancelled) setError('Could not load recent runs'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [viewType, refreshKey]);

  const handleClear = async () => {
    try {
      await clearRecentRuns(viewType);
      setRuns([]);
    } catch {
      setError('Could not clear history');
    }
  };

  const count = runs.length;
  // Don't show a count until the fetch has resolved. Before that, `runs` is still
  // the empty initial state, so a bare "Recent Runs (0)" flashes on first load and
  // only flips to "(N)" once the request returns — the "(0) then (4)" mismatch the
  // header showed. While loading (or on error) omit the number entirely.
  const headerText = loading
    ? 'Recent Runs (…)'
    : error
    ? 'Recent Runs'
    : `Recent Runs (${count})`;
  return (
    <ExpandableSection headerText={headerText} variant="container">
      {loading ? (
        <Box textAlign="center" padding="l"><StatusIndicator type="loading">Loading…</StatusIndicator></Box>
      ) : error ? (
        <Box textAlign="center" color="text-status-error" padding="l">{error}</Box>
      ) : count === 0 ? (
        <Box textAlign="center" color="text-body-secondary" padding="l">No recent runs</Box>
      ) : (
        <SpaceBetween size="s">
          <Table
            variant="embedded"
            sortingDisabled
            stripedRows
            columnDefinitions={[
              {
                id: 'time',
                header: 'Time',
                cell: (item: RecentRunItem) => formatRelativeTime(item.submitted_at),
                width: 120,
              },
              {
                id: 'label',
                header: 'Run',
                cell: (item: RecentRunItem) => item.label || item.run_id,
              },
            ]}
            items={runs}
            onRowClick={({ detail }) => onLoadRun(detail.item)}
          />
          <Box textAlign="right">
            <Button variant="link" onClick={handleClear}>Clear history</Button>
          </Box>
        </SpaceBetween>
      )}
    </ExpandableSection>
  );
}
